import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useWsNavigate } from "../hooks/useWsNavigate";
import { aiTestingAgentAPI, callObservationsAPI, promptOptimizerAPI } from "../services/api";
import PageHeader from "../components/layout/PageHeader";
import { useAuth } from "../contexts/AuthContext";
import { useAgent } from "../contexts/AgentContext";
import { Sparkles, Loader2, Info } from "lucide-react";
import { LITELLM_MODEL_OPTIONS, DEFAULT_LITELLM_MODEL } from "../constants/providerCatalogue";
import CallPicker from "../components/prompt-optimizer/CallPicker";
import DiagnosisReview from "../components/prompt-optimizer/DiagnosisReview";
import { cn } from "../lib/utils";
import { usePostHog } from "@posthog/react";

// ── Main component ─────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 3000;

const NewPromptOptimization = () => {
  // ── Shared data ────────────────────────────────────────────────────────────
  const [runs, setRuns] = useState([]);
  const [observations, setObservations] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  // ── Wizard state (2 steps: 1 = select+configure, 2 = diagnosis) ────────────
  const [step, setStep] = useState(1);

  // Step 1 — selection + configuration
  const [mode, setMode] = useState("fix_failing"); // "fix_failing" | "reinforce_good"
  const [selectedItems, setSelectedItems] = useState([]); // [{type, id}]
  const [preferredModel, setPreferredModel] = useState(DEFAULT_LITELLM_MODEL);
  const [constraints, setConstraints] = useState("");

  // Analyze → diagnosis
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);

  // Step 2 — diagnosis
  const [optimizationId, setOptimizationId] = useState(null);
  const [diagnosis, setDiagnosis] = useState(null);

  // Resume — selection to restore once the call lists have loaded
  const [pendingSelection, setPendingSelection] = useState(null);

  // ── Contexts ───────────────────────────────────────────────────────────────
  const { currentWorkspace } = useAuth();
  const { selectedAgent, setSelectedAgent, agents } = useAgent();
  const navigate = useWsNavigate();
  const posthog = usePostHog();
  const [searchParams] = useSearchParams();

  const pollRef = useRef(null);
  const mountedRef = useRef(true);
  const resumedRef = useRef(false);

  // ── Fetch runs + observations ──────────────────────────────────────────────
  useEffect(() => {
    if (currentWorkspace?.id && selectedAgent?.id) {
      fetchData();
    } else {
      setRuns([]);
      setObservations([]);
    }
  }, [currentWorkspace, selectedAgent?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // ── Resume an in-progress optimization (?optimization=ID) ──────────────────
  useEffect(() => {
    const resumeId = searchParams.get("optimization");
    if (!resumeId || resumedRef.current || !currentWorkspace?.id) return;
    resumedRef.current = true;
    (async () => {
      try {
        const opt = await promptOptimizerAPI.getOptimization(resumeId, currentWorkspace.id);
        if (!mountedRef.current || !opt) return;
        setOptimizationId(opt.id);
        setPreferredModel(opt.preferred_model || DEFAULT_LITELLM_MODEL);
        setConstraints(opt.metadata?.improvement_constraints || "");
        setMode(opt.metadata?.mode || "fix_failing");
        // Switch to the optimization's agent so its calls load on step 1.
        if (opt.agent?.id && opt.agent.id !== selectedAgent?.id) {
          const match = agents.find((a) => a.id === opt.agent.id);
          if (match) setSelectedAgent(match);
        }
        // Restore the call selection after the lists finish loading.
        setPendingSelection(
          (opt.source_ids || []).map((s) => ({ type: s.type ?? s.Type, id: s.id ?? s.ID }))
        );
        const hasDiagnosis = opt.diagnosis?.gaps?.length > 0 || opt.status === "diagnosed";
        if (hasDiagnosis && opt.diagnosis) {
          setDiagnosis(opt.diagnosis);
          setStep(2);
        } else if (opt.status === "diagnosing") {
          setAnalyzing(true);
          startPolling(opt.id);
        } else if (opt.status === "failed") {
          setAnalyzeError("The previous attempt failed. Adjust your selection and analyze again.");
        }
      } catch (e) {
        console.error("Resume error:", e);
      }
    })();
  }, [searchParams, currentWorkspace?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply a resumed selection once the call lists are loaded.
  useEffect(() => {
    if (!loadingData && pendingSelection) {
      setSelectedItems(pendingSelection);
      setPendingSelection(null);
    }
  }, [loadingData, pendingSelection]);

  const fetchData = async () => {
    setLoadingData(true);
    setSelectedItems([]);
    try {
      const [runsRes, obsRes] = await Promise.all([
        aiTestingAgentAPI.getAgentRuns(selectedAgent.id, currentWorkspace.id),
        callObservationsAPI.getCallObservations(currentWorkspace.id, {
          agent_id: selectedAgent.id,
        }),
      ]);
      setRuns(runsRes || []);
      const obsData = obsRes?.data?.call_observations || obsRes?.data || [];
      setObservations(Array.isArray(obsData) ? obsData : []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoadingData(false);
    }
  };

  // ── Polling: wait for diagnosis to complete, then advance to Step 2 ────────
  const startPolling = (optId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const status = await promptOptimizerAPI.getOptimizationStatus(optId, currentWorkspace.id);
        if (status?.status === "diagnosed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          const full = await promptOptimizerAPI.getOptimization(optId, currentWorkspace.id);
          if (!mountedRef.current) return;
          setDiagnosis(full?.diagnosis || { summary: "", gaps: [] });
          setAnalyzing(false);
          setStep(2);
        } else if (status?.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (!mountedRef.current) return;
          setAnalyzeError("Analysis failed. Please try again.");
          setAnalyzing(false);
        }
      } catch (pollErr) {
        console.error("Polling error:", pollErr);
      }
    }, POLL_INTERVAL_MS);
  };

  // ── Analyze (Step 1 → polling → Step 2) ────────────────────────────────────
  const runAnalyze = async (existingId) => {
    setAnalyzing(true);
    setAnalyzeError(null);

    try {
      let optId = existingId;

      if (!optId) {
        const created = await promptOptimizerAPI.createOptimization(
          selectedAgent.id,
          selectedItems,
          preferredModel,
          constraints,
          currentWorkspace.id,
          mode
        );
        optId = created.id;
        if (!mountedRef.current) return;
        setOptimizationId(optId);
        posthog?.capture('prompt_optimization_started', {
          optimization_id: optId,
          agent_id: selectedAgent?.id,
          mode,
        });
      }

      await promptOptimizerAPI.diagnose(
        optId,
        { sourceItems: selectedItems, preferredModel, constraints, mode },
        currentWorkspace.id
      );

      startPolling(optId);
    } catch (err) {
      console.error("Analyze error:", err);
      if (!mountedRef.current) return;
      setAnalyzeError(err?.message || "Failed to start analysis.");
      setAnalyzing(false);
    }
  };

  const handleAnalyze = () => runAnalyze(null);

  // Re-analyze from Step 2 — reuse the same optimization id.
  const handleRegenerate = () => {
    setDiagnosis(null);
    setStep(1);
    runAnalyze(optimizationId);
  };

  const handleGenerate = async (selectedGapIds, editedGaps) => {
    try {
      await promptOptimizerAPI.generate(
        optimizationId,
        selectedGapIds,
        currentWorkspace.id,
        editedGaps
      );
      navigate(`/prompt-optimizer/status/${optimizationId}`, {
        state: { clientId: currentWorkspace.id },
      });
    } catch (err) {
      console.error("Generate error:", err);
      alert(`Failed to generate rewrite: ${err?.message || "Unknown error"}`);
    }
  };

  const canAnalyze = selectedItems.length > 0 && !analyzing;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="px-4">
      <PageHeader
        title="New Prompt Optimization"
        description="Analyze call quality issues then generate a targeted prompt rewrite."
        breadcrumbs={[
          { label: "Prompt Optimizer", path: "/prompt-optimizer" },
          { label: "New Optimization", path: "/prompt-optimizer/new" },
        ]}
      />

      <div className="mt-6">
        {!selectedAgent?.id ? (
          <div className="text-center py-8 text-muted-foreground">
            Select an agent from the header to get started.
          </div>
        ) : (
          <>
            {/* ── Step 1: Select + configure (full width) ──────────────────── */}
            {step === 1 && (
              <>
              {/* Mode selector */}
              <div className="mb-6 bg-card border border-border rounded-lg p-4">
                <p className="text-sm font-semibold text-foreground mb-2">What do you want to do?</p>
                <div className="flex flex-wrap gap-3">
                  {[
                    {
                      id: "fix_failing",
                      title: "Failure-driven",
                      desc: "Pick calls with failed metrics; the rewrite fixes what made them fail.",
                      info: "Learns from failures. Diagnoses what the current instruction is missing on calls where metrics failed or were weak, then rewrites it to close those gaps. Driven primarily by the failed metrics.",
                    },
                    {
                      id: "reinforce_good",
                      title: "Exemplar-driven",
                      desc: "Pick calls that went well; the rewrite makes the agent reliably reproduce them.",
                      info: "Learns from exemplars (good calls). Extracts the strong behaviours these calls demonstrate — from both the transcripts and the metrics — and rewrites the instruction so the agent reliably reproduces them on every call.",
                    },
                  ].map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setMode(m.id)}
                      className={cn(
                        "text-left p-3 rounded-lg border transition-colors max-w-xs",
                        mode === m.id
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-input"
                      )}
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-foreground">{m.title}</span>
                        <span
                          title={m.info}
                          className="inline-flex text-muted-foreground hover:text-foreground"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Info className="w-3.5 h-3.5" />
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{m.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                {/* Calls — spans two columns */}
                <div className="lg:col-span-2 bg-card border border-border rounded-lg p-6 flex flex-col gap-5">
                  <div>
                    <h2 className="text-sm font-semibold text-foreground mb-1">
                      {mode === "reinforce_good" ? "Select good calls" : "Select calls with failed metrics"}
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      {mode === "reinforce_good"
                        ? "Pick up to 5 calls that went well — from batch runs and/or call observations — for the agent to emulate. Click “View call” to inspect any call."
                        : "Pick up to 5 calls with failed metrics — from batch runs and/or call observations — to optimize from. Click “View call” to inspect any call."}
                    </p>
                  </div>

                  {loadingData ? (
                    <div className="flex justify-center py-16">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <CallPicker
                      runs={runs.filter((r) => r.status === "completed")}
                      observations={observations}
                      value={selectedItems}
                      onChange={setSelectedItems}
                      source={
                        runs.filter((r) => r.status === "completed").length === 0 &&
                        observations.length > 0
                          ? "observations"
                          : "runs"
                      }
                    />
                  )}
                </div>

                {/* Configuration — sticky side panel */}
                <div className="bg-card border border-border rounded-lg p-6 flex flex-col gap-5 lg:sticky lg:top-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-muted-foreground" />
                      <h2 className="text-sm font-semibold text-foreground">Optimization inputs</h2>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Context we use to tailor the rewrite to your agent.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">
                      Preferred LLM
                    </label>
                    <select
                      value={preferredModel}
                      onChange={(e) => setPreferredModel(e.target.value)}
                      className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                    >
                      {LITELLM_MODEL_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      The rewrite is tuned to match this model’s style.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">
                      Improvement constraints
                    </label>
                    <textarea
                      value={constraints}
                      onChange={(e) => setConstraints(e.target.value)}
                      placeholder="e.g., Focus on authentication flow, improve greeting tone…"
                      rows={5}
                      className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary resize-none"
                    />
                  </div>

                  {analyzeError && <p className="text-sm text-destructive">{analyzeError}</p>}

                  <button
                    type="button"
                    onClick={handleAnalyze}
                    disabled={!canAnalyze}
                    className={cn(
                      "flex items-center justify-center gap-2 w-full px-5 py-2.5 text-sm font-medium text-primary-foreground bg-primary rounded-md transition-colors",
                      !canAnalyze ? "opacity-50 cursor-not-allowed" : "hover:bg-primary/90"
                    )}
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Analyzing calls…
                      </>
                    ) : (
                      "Analyze calls"
                    )}
                  </button>
                  {selectedItems.length === 0 && (
                    <p className="text-[11px] text-muted-foreground text-center -mt-2">
                      Select at least one call to continue.
                    </p>
                  )}
                </div>
              </div>
              </>
            )}

            {/* ── Step 2: Diagnosis review ─────────────────────────────────── */}
            {step === 2 && diagnosis && (
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="mb-6">
                  <h2 className="text-sm font-semibold text-foreground mb-1">Diagnosis</h2>
                  <p className="text-xs text-muted-foreground">
                    Review the gaps found in the selected calls. Edit, uncheck, or add gaps,
                    then generate a rewrite.
                  </p>
                </div>

                <DiagnosisReview
                  diagnosis={diagnosis}
                  onRegenerate={handleRegenerate}
                  onGenerate={handleGenerate}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NewPromptOptimization;
